/**
 * Doom's Update 01 PEN-08: the caller's one saved Penelope default (GET/PUT /api/penelope/profile). Configuration
 * only — the relay strips the team (lean), the side permissions, the variants, the mode and arming, and refuses a
 * value this box would not run rather than clamping it. `startMode` is the mode a one-click Start on a dashboard row
 * runs in; a LIVE one is confirmed once, here, when it is saved (Josh 2026-10-12). Saving changes future launches only.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import type { PenelopeConfig } from './usePenelope';
import { refreshPenelopeMine } from './usePenelopeMine';
import type { PenelopeSport } from './usePenelopeSides';

/** A saved default is for one sport, or for 'all' (the fallback a sport without its own row starts from). */
export type PenelopeProfileSport = PenelopeSport | 'all';

export interface PenelopeBound { min: number; max: number; int: boolean; scale?: number }
export interface PenelopeFieldError { field: string; error: string }
export interface PenelopeSavedProfile {
  /** Which default this is (relay ≥ 2026-10-14; older relays send none = 'all'). */
  sport?: PenelopeProfileSport;
  config: Partial<PenelopeConfig>;
  startMode: 'live' | 'shadow';
  version: number;
  updatedAt: string;
  updatedBy: string | null;
}
export interface PenelopeProfileRead {
  /** The sport this read answered for. */
  sport?: PenelopeProfileSport;
  /** The default a game of that sport starts from: its own, else the all-sports one. */
  profile: PenelopeSavedProfile | null;
  /** Every saved default by sport. */
  profiles?: Partial<Record<PenelopeProfileSport, PenelopeSavedProfile>>;
  /** What a one-click Start would run from it: the saved knobs on the relay's defaults, both sides, no lean. */
  effective: PenelopeConfig | null;
  /** Why the saved default would not run on this box any more (a lowered ceiling); empty when it would. */
  invalid: PenelopeFieldError[];
  /** Every numeric knob's legal range on this box. */
  bounds: Record<string, PenelopeBound>;
  defaults: PenelopeConfig | null;
  liveAllowed: boolean;
  liveGranted: boolean;
}

export interface PenelopeProfileControl {
  data: PenelopeProfileRead | null;
  error: string | null;
  /** Per-field refusals from the last save, for the inputs to mark. */
  fieldErrors: PenelopeFieldError[];
  busy: boolean;
  /** Save `config` (only its configuration keys are kept) as the default for `sport` ('all' when omitted); a live
   *  start mode needs `confirmLive`. */
  save: (config: Partial<PenelopeConfig>, startMode: 'live' | 'shadow', confirmLive: boolean, sport?: PenelopeProfileSport) => Promise<boolean>;
  reload: () => Promise<void>;
}

/** `enabled` false reads nothing (no access). `sport` names the game's sport, so `profile` is the default that game
 *  would start from (its own sport's, else the all-sports one). */
export function usePenelopeProfile(enabled: boolean, sport: PenelopeSport | null = null): PenelopeProfileControl {
  const [data, setData] = useState<PenelopeProfileRead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PenelopeFieldError[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await apiFetch(sport ? `/api/penelope/profile?sport=${sport}` : '/api/penelope/profile');
      const body = (await res.json().catch(() => ({}))) as PenelopeProfileRead & { error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setData(body);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [enabled, sport]);
  useEffect(() => { void reload(); }, [reload]);

  const save = useCallback(async (config: Partial<PenelopeConfig>, startMode: 'live' | 'shadow', confirmLive: boolean, forSport: PenelopeProfileSport = 'all') => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/penelope/profile', {
        method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ config, startMode, confirmLive, sport: forSport }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; fields?: PenelopeFieldError[] };
      if (!res.ok) {
        setFieldErrors(body.fields ?? []);
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setFieldErrors([]);
      setError(null);
      await reload();
      void refreshPenelopeMine();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [reload]);

  return { data, error, fieldErrors, busy, save, reload };
}
